<template>
  <div class="business-calendar">
    <div class="calendar-heading">
      <button class="month-arrow" type="button" :aria-label="$t('businessCalendar.previousMonth')" @click="changeMonth(-1)">
        <i class="el-icon-d-arrow-left"></i>
      </button>
      <div class="month-title">{{ formatMonth(leftMonth) }}</div>
      <div class="month-title">{{ formatMonth(rightMonth) }}</div>
      <button class="month-arrow" type="button" :aria-label="$t('businessCalendar.nextMonth')" @click="changeMonth(1)">
        <i class="el-icon-d-arrow-right"></i>
      </button>
    </div>

    <div class="calendar-months">
      <el-calendar v-model="leftMonth" :first-day-of-week="1">
        <template slot="dateCell" slot-scope="{ data }">
          <el-tooltip
            :content="dayDescription(data)"
            :disabled="!dayDescription(data)"
            placement="top"
            effect="light"
            class="calendar-tooltip-trigger"
            popper-class="business-calendar-tooltip"
          >
            <div class="calendar-day" :class="dayClass(data)" @click.stop.prevent>{{ dayNumber(data.day) }}</div>
          </el-tooltip>
        </template>
      </el-calendar>
      <el-calendar v-model="rightMonth" :first-day-of-week="1">
        <template slot="dateCell" slot-scope="{ data }">
          <el-tooltip
            :content="dayDescription(data)"
            :disabled="!dayDescription(data)"
            placement="top"
            effect="light"
            class="calendar-tooltip-trigger"
            popper-class="business-calendar-tooltip"
          >
            <div class="calendar-day" :class="dayClass(data)" @click.stop.prevent>{{ dayNumber(data.day) }}</div>
          </el-tooltip>
        </template>
      </el-calendar>
    </div>

    <div class="calendar-footer">
      <div class="calendar-legend">
        <span><i class="legend-swatch legend-rakumart"></i>{{ $t('businessCalendar.rakumartHoliday') }}</span>
        <span><i class="legend-swatch legend-substitute"></i>{{ $t('businessCalendar.substituteWork') }}</span>
        <span><i class="legend-swatch legend-office"></i>{{ $t('businessCalendar.officeHoliday') }}</span>
      </div>
      <button class="calendar-today" type="button" @click="goToToday">{{ $t('businessCalendar.today') }}</button>
    </div>
    <div v-if="loadError" class="calendar-error">{{ $t('businessCalendar.loadError') }}</div>
  </div>
</template>

<script>
export default {
  name: 'BusinessCalendar',
  data() {
    const now = new Date();
    return {
      leftMonth: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
      rightMonth: this.shiftMonth(now, 1),
      dayStatuses: {},
      loadError: false
    };
  },
  mounted() {
    document.addEventListener('mouseover', this.handleTooltipMouseOver, true);
    document.addEventListener('mouseout', this.handleTooltipMouseOut, true);
    this.fetchCalendar();
  },
  beforeDestroy() {
    document.removeEventListener('mouseover', this.handleTooltipMouseOver, true);
    document.removeEventListener('mouseout', this.handleTooltipMouseOut, true);
  },
  methods: {
    getTooltipPopper(target) {
      const element = target && target.nodeType === 1 ? target : target && target.parentElement;
      return element && element.closest ? element.closest('.business-calendar-tooltip') : null;
    },
    handleTooltipMouseOver(event) {
      const popper = this.getTooltipPopper(event.target);
      if (!popper || (event.relatedTarget && popper.contains(event.relatedTarget))) return;
      this.$emit('tooltip-enter');
    },
    handleTooltipMouseOut(event) {
      const popper = this.getTooltipPopper(event.target);
      if (!popper || (event.relatedTarget && popper.contains(event.relatedTarget))) return;
      this.$emit('tooltip-leave', event.relatedTarget);
    },
    shiftMonth(date, offset) {
      const targetMonth = new Date(date.getFullYear(), date.getMonth() + offset, 1);
      const lastDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate();
      return new Date(targetMonth.getFullYear(), targetMonth.getMonth(), Math.min(date.getDate(), lastDay));
    },
    changeMonth(offset) {
      const nextLeft = this.shiftMonth(this.leftMonth, offset);
      this.leftMonth = nextLeft;
      this.rightMonth = this.shiftMonth(nextLeft, 1);
    },
    goToToday() {
      const now = new Date();
      this.leftMonth = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      this.rightMonth = this.shiftMonth(now, 1);
    },
    formatMonth(date) {
      const locale = this.$i18n.locale === 'ja-JP' ? 'ja-JP' : 'zh-CN';
      return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long' }).format(date);
    },
    dayNumber(day) {
      return day.slice(-2);
    },
    dayDescription(data) {
      if (data.type !== 'current-month') return '';
      const record = this.dayStatuses[data.day];
      return record && record.holidayName ? record.holidayName : '';
    },
    dayClass(data) {
      const classes = [];
      if (data.type !== 'current-month') classes.push('is-outside-month');
      else if (data.day === this.formatDateKey(new Date())) classes.push('is-today');
      if (data.type !== 'current-month') return classes;
      const record = this.dayStatuses[data.day];
      const status = record && record.status;
      if (status) {
        if (status === 'holiday-rest') classes.push('is-office-holiday');
        else if (status === 'adjustment' || status === 'holiday-work') classes.push('is-substitute-work');
        else if (status === 'rest' || status === 'weekend') classes.push('is-rakumart-holiday');
      } else {
        const date = new Date(`${data.day}T00:00:00`);
        if (date.getDay() === 0) classes.push('is-rakumart-holiday');
      }
      return classes;
    },
    formatDateKey(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    fetchCalendar() {
      this.$http.get('system/configurationCalendar/list', {
        params: { pageNum: 1, pageSize: 1000 },
        headers: { noLoading: true }
      }).then(response => {
        if (!response || !response.data || response.data.code !== 200) {
          throw new Error('Calendar request failed');
        }
        const result = response && response.data && response.data.data;
        const rows = result && result.data && result.data.list
          ? result.data.list
          : result && result.list
            ? result.list
            : [];
        const statuses = {};
        rows.forEach(row => {
          const date = String(row.date || row.dateStr || '').slice(0, 10);
          if (date) statuses[date] = { status: row.status, holidayName: row.holidayName || '' };
        });
        this.dayStatuses = statuses;
      }).catch(() => {
        this.loadError = true;
      });
    }
  }
};
</script>

<style lang="less" scoped>
.business-calendar {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px 8px;
  color: #303133;
  background: #fff;
}

.calendar-heading {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) minmax(0, 1fr) 28px;
  align-items: center;
  min-height: 34px;
  margin-bottom: 4px;
}

.month-title {
  text-align: center;
  font-size: 16px;
  font-weight: 500;
}

.month-arrow {
  padding: 4px;
  border: 0;
  color: #606266;
  background: transparent;
  cursor: pointer;
}

.calendar-months {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

/deep/ .el-calendar__header {
  display: none;
}

/deep/ .el-calendar__body {
  padding: 0;
}

/deep/ .el-calendar-table {
  pointer-events: auto;
}

/deep/ .el-calendar-table thead th {
  padding: 6px 0;
  color: #606266;
  background: #e5e5e5;
  text-align: center !important;
  vertical-align: middle;
}

/deep/ .el-calendar-table .el-calendar-day {
  height: 35px;
  padding: 0;
}

/deep/ .el-calendar-table td {
  border: 0 !important;
}

/deep/ .el-calendar-table td.is-selected {
  background-color: transparent !important;
}

.calendar-day {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #222;
  font-size: 15px;
}

.calendar-day.is-outside-month {
  color: #c0c4cc;
}

.calendar-day.is-rakumart-holiday {
  color: #fff;
  background: #bd2931;
}

.calendar-day.is-substitute-work {
  color: #222;
  background: #ffdc00;
}

.calendar-day.is-office-holiday {
  color: #fff;
  background: #3295f5;
}

.calendar-day.is-today {
  box-shadow: inset 0 0 0 2px #1685e5;
}

.calendar-tooltip-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.calendar-footer {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding-top: 10px;
}

.calendar-legend {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  white-space: nowrap;
}

.calendar-today {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-25%);
  padding: 4px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  color: #606266;
  background: #fff;
  cursor: pointer;
}

.calendar-today:hover {
  color: #409eff;
  border-color: #c6e2ff;
  background: #ecf5ff;
}

.calendar-legend > span {
  display: inline-flex;
  align-items: center;
  font-size: 14px;
}

.legend-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 6px;
  border-radius: 2px;
}

.legend-rakumart { background: #bd2931; }
.legend-substitute { background: #ffdc00; }
.legend-office { background: #3295f5; }

.calendar-error {
  padding-top: 6px;
  color: #f56c6c;
  text-align: center;
  font-size: 12px;
}
</style>

<style lang="less">
.business-calendar-tooltip {
  max-width: 320px;
  line-height: 1.5;
  font-weight: 600;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
